// 화면 17 — TanStack Query 훅 (폴링 60s, PRD §5.7.4 / ADR-DASH-06 권위).
import { useQuery } from '@tanstack/react-query';
import { ApiError } from '../../api/client';
import {
  getLeadingSectorDivergence,
  getLeadingSectorHistory,
} from '../../api/leadingSectorsApi';
import type {
  DivergencePanelResponse,
  SectorHistoryMode,
  SectorHistoryResponse,
} from './leadingSectors.types';

const POLL_MS = 60_000;

// 권위: ADR-DASH-06 — WebSocket 미사용. 일 단위 데이터 → 폴링 only 60s.
export function useSmoothedHistory(from: string, to: string) {
  return useQuery<SectorHistoryResponse>({
    queryKey: ['leading-sectors', 'history', 'smoothed', from, to],
    queryFn: () =>
      getLeadingSectorHistory({ from, to, mode: 'smoothed', includeSparkline: false }),
    refetchInterval: POLL_MS,
    staleTime: POLL_MS - 1000,
  });
}

export function useDailyTop3Sparkline(from: string, to: string) {
  return useQuery<SectorHistoryResponse>({
    queryKey: ['leading-sectors', 'history', 'daily_top3', 'sparkline', from, to],
    queryFn: () =>
      getLeadingSectorHistory({ from, to, mode: 'daily_top3', includeSparkline: true }),
    refetchInterval: POLL_MS,
    staleTime: POLL_MS - 1000,
  });
}

export function useDivergencePanel(date: string) {
  return useQuery<DivergencePanelResponse, ApiError>({
    queryKey: ['leading-sectors', 'divergence', date],
    queryFn: () => getLeadingSectorDivergence(date),
    refetchInterval: POLL_MS,
    staleTime: POLL_MS - 1000,
    // 404 (휴장 또는 미적재) / 503 (O-DB-7 미결착) 은 재시도 무의미.
    retry: (failureCount, err) => {
      const status = (err as ApiError | undefined)?.httpStatus;
      if (status && [400, 404, 503].includes(status)) return false;
      return failureCount < 1;
    },
  });
}

// 모드별 ARCH 합의된 lookback cap (AC-DB-PRD-16 — days <= 90).
export const MAX_LOOKBACK_DAYS = 90;

// 사용처 noop — 단순 enum 노출.
export const _MODES: SectorHistoryMode[] = ['smoothed', 'daily_top3', 'both'];
